import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, Timer, Trophy, ArrowRight, CalendarDays } from "lucide-react";

const DIFFICULTIES = [
  {
    key: "easy",
    label: "Easy",
    blurb: "Short, cosy idioms. 2 to 4 words.",
    volume: "Volume I",
  },
  {
    key: "normal",
    label: "Normal",
    blurb: "Classic British sayings. 4 to 6 words.",
    volume: "Volume II",
  },
  {
    key: "hard",
    label: "Hard",
    blurb: "Proverbs and tongue-twisters. 6+ words.",
    volume: "Volume III",
  },
];

export default function Home() {
  const navigate = useNavigate();

  return (
    <div
      className="relative min-h-screen w-full"
      data-testid="home-page"
    >
      <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 py-14 md:py-24">
        {/* Masthead */}
        <motion.header
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="border-b-4 border-[#1A1A1A] pb-6 mb-12 md:mb-20"
        >
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#EB5E28] border-2 border-[#1A1A1A] flex items-center justify-center shadow-[3px_3px_0px_#1A1A1A]">
                <BookOpen className="w-5 h-5 text-[#F4F1EA]" />
              </div>
              <span className="text-xs font-sans-ui uppercase tracking-[0.24em] font-semibold text-[#6B6A68]">
                The Idiom Press · Est. 2026
              </span>
            </div>
            <span className="text-xs font-sans-ui uppercase tracking-[0.2em] text-[#6B6A68]">
              Penny Dreadful Edition
            </span>
          </div>
        </motion.header>

        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-10 md:mb-14"
        >
          <p className="font-sans-ui text-xs uppercase tracking-[0.3em] text-[#EB5E28] font-semibold mb-4">
            A Parlour Game
          </p>
          <h1
            className="font-serif-display text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight text-[#1A1A1A]"
            data-testid="home-title"
          >
            Fraseology.
          </h1>
          <p className="font-serif-display italic text-2xl md:text-3xl text-[#6B6A68] mt-4 max-w-2xl">
            Rearrange the scattered letters. Recover the phrase. Mind the clock.
          </p>
        </motion.div>

        {/* How it works strip */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.25 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12 md:mb-16"
        >
          {[
            {
              key: "british",
              icon: <BookOpen className="w-5 h-5" />,
              title: "British idioms",
              body: "All phrases are well-known British English.",
            },
            {
              key: "clock",
              icon: <Timer className="w-5 h-5" />,
              title: "Beat the clock",
              body: "Solve in under a minute for a hefty bonus.",
            },
            {
              key: "score",
              icon: <Trophy className="w-5 h-5" />,
              title: "Score points",
              body: "Every correct letter banked adds to your score.",
            },
          ].map((item) => (
            <div
              key={item.key}
              className="border-2 border-[#1A1A1A] bg-white/60 p-5 shadow-[4px_4px_0px_#1A1A1A]"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 bg-[#1A1A1A] text-[#F4F1EA] flex items-center justify-center">
                  {item.icon}
                </div>
                <h3 className="font-serif-display text-xl font-semibold">
                  {item.title}
                </h3>
              </div>
              <p className="font-sans-ui text-sm text-[#6B6A68] leading-relaxed">
                {item.body}
              </p>
            </div>
          ))}
        </motion.div>

        {/* Daily challenge */}
        <motion.button
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          whileHover={{ y: -4 }}
          onClick={() => navigate("/play/daily")}
          data-testid="difficulty-daily"
          className="group block w-full mb-6 text-left bg-[#EB5E28] text-[#F4F1EA] border-2 border-[#1A1A1A] p-6 md:p-7 shadow-[6px_6px_0px_#1A1A1A] hover:shadow-[8px_8px_0px_#1A1A1A] transition-all relative overflow-hidden"
        >
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <CalendarDays className="w-4 h-4" />
                <span className="font-sans-ui text-[10px] uppercase tracking-[0.3em] font-bold">
                  Today&apos;s edition
                </span>
              </div>
              <h2 className="font-serif-display text-4xl md:text-5xl font-semibold leading-tight">
                Daily challenge
              </h2>
              <p className="font-sans-ui text-sm md:text-base text-[#F4F1EA]/80 mt-2 max-w-md">
                One phrase, everyone, same UTC day. See where you rank against
                today&apos;s players.
              </p>
            </div>
            <div className="flex items-center gap-2 self-center">
              <span className="font-serif-display italic text-sm">play now</span>
              <ArrowRight className="w-5 h-5 -rotate-45 group-hover:rotate-0 transition-transform" />
            </div>
          </div>
        </motion.button>

        {/* Difficulty cards */}
        <div className="mb-6">
          <p className="font-sans-ui text-xs uppercase tracking-[0.24em] font-semibold text-[#6B6A68]">
            Or choose your volume
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {DIFFICULTIES.map((d, i) => (
            <motion.button
              key={d.key}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.35 + i * 0.08 }}
              whileHover={{ y: -4 }}
              onClick={() => navigate(`/play/${d.key}`)}
              data-testid={`difficulty-${d.key}`}
              className="group text-left relative overflow-hidden bg-[#1A1A1A] text-[#F4F1EA] border-2 border-[#1A1A1A] p-6 md:p-7 shadow-[6px_6px_0px_#EB5E28] hover:shadow-[8px_8px_0px_#EB5E28] transition-all"
            >
              <div className="flex items-start justify-between mb-6">
                <span className="font-sans-ui text-xs uppercase tracking-[0.24em] text-[#EB5E28] font-bold">
                  {d.volume}
                </span>
                <ArrowRight className="w-5 h-5 -rotate-45 group-hover:rotate-0 transition-transform" />
              </div>
              <h2 className="font-serif-display text-4xl md:text-5xl font-semibold mb-3">
                {d.label}
              </h2>
              <p className="font-sans-ui text-sm text-[#F4F1EA]/70 leading-relaxed">
                {d.blurb}
              </p>
              <div className="mt-8 pt-5 border-t border-[#F4F1EA]/20 flex items-center justify-between">
                <span className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-[#F4F1EA]/50">
                  Press to play
                </span>
                <span className="font-serif-display italic text-sm text-[#EB5E28]">
                  no. {String(i + 1).padStart(2, "0")}
                </span>
              </div>
            </motion.button>
          ))}
        </div>

        <footer className="mt-16 md:mt-24 pt-6 border-t-2 border-[#1A1A1A]/20 flex items-center justify-between flex-wrap gap-3">
          <span className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-[#6B6A68]">
            Printed for the discerning puzzler
          </span>
          <span className="font-serif-display italic text-sm text-[#6B6A68]">
            pg. 001
          </span>
        </footer>
      </div>
    </div>
  );
}
